/**
 * EraCertificates — 9 era certs + Master tier panel.
 *
 * Lead's brief (final sprint):
 *   Each era cert requires (a) every locked chain in that era completed
 *   AND (b) at least one engagement with a contradicting_primary_source.
 *   Master cert unlocks when all 9 era certs are active.
 *
 * Honest-date principle (methodology hold #4): criteria not yet met show
 * "Keep walking" copy, not red X. Methodology is a path, not a test.
 *
 * Era grammar: each row uses its era's accent color band. Master row
 * combines all 9 accents in a thin chronological stripe.
 */
import { useEffect, useState } from 'react';
import { Loader2, Download, ExternalLink, CheckCircle2, Circle, Award, Layers } from 'lucide-react';
import api from '../lib/api';
import CertPremiumActions from './CertPremiumActions';

const ERA_STYLE = {
  deep_time:    { primary: '#7B6B3C', accent: '#D4AB52', subtitle: 'Bone · Sandstone · Footprint' },
  ancient:      { primary: '#A38659', accent: '#FFD68A', subtitle: 'Papyrus · Stone · Cuneiform' },
  classical:    { primary: '#C9A86A', accent: '#FFE6A3', subtitle: 'Marble · Vellum · Classical' },
  medieval:     { primary: '#8C6E3F', accent: '#E0B872', subtitle: 'Vellum · Iron · Manuscript' },
  early_modern: { primary: '#B08D5B', accent: '#FFD27D', subtitle: 'Quill · Press · Print' },
  colonial:     { primary: '#9C7649', accent: '#E8B973', subtitle: 'Ink · Sail · Treaty' },
  industrial:   { primary: '#7C7466', accent: '#C9C0B0', subtitle: 'Iron · Steam · Soot' },
  modern:       { primary: '#5A6B85', accent: '#A0C0E5', subtitle: 'Wire · Voltage · Newsprint' },
  contemporary: { primary: '#00C2D6', accent: '#D4A84A', subtitle: 'Glass · Network · Now' },
};

export default function EraCertificates() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [claimingEra, setClaimingEra] = useState(null);
  const [claimingMaster, setClaimingMaster] = useState(false);
  const [msg, setMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const reload = async () => {
    setBusy(true);
    setErrorMsg('');
    try {
      const r = await api.get('/certificates/eras');
      setData(r.data);
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Could not load era certificate state.');
    }
    setBusy(false);
  };
  useEffect(() => { reload(); }, []);

  const claimEra = async (era) => {
    setClaimingEra(era);
    setMsg('');
    try {
      const r = await api.post(`/certificates/era/${era}/claim`);
      if (r.data?.issued || r.data?.certificate_id) {
        setMsg(`${era.replace('_', ' ')} claimed.`);
        await reload();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setMsg(typeof detail === 'object' ? detail.message : (detail || 'Claim failed.'));
    }
    setClaimingEra(null);
  };

  const claimMaster = async () => {
    setClaimingMaster(true);
    setMsg('');
    try {
      const r = await api.post('/certificates/master/claim');
      if (r.data?.issued || r.data?.certificate_id) {
        setMsg('Master certificate claimed.');
        await reload();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setMsg(typeof detail === 'object' ? detail.message : (detail || 'Master claim failed.'));
    }
    setClaimingMaster(false);
  };

  if (busy && !data) {
    return <div className="p-6"><Loader2 size={20} className="animate-spin text-[#2F8A92]" /></div>;
  }
  if (errorMsg && !data) {
    return <div className="p-6 font-mono text-sm text-red-300" data-testid="era-cert-error">{errorMsg}</div>;
  }

  const eras = data?.eras || [];
  const master = data?.master || {};

  return (
    <div className="space-y-8" data-testid="era-certificates-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Layers size={16} className="text-[#D4A84A]" />
        <div>
          <div className="font-historical text-2xl md:text-3xl text-[#D4A84A]">Era Certificates</div>
          <div className="font-mono text-xs md:text-sm text-white/55 mt-1">
            Walk the era. Engage the contradiction. Earn the certificate. Nine eras → one Master credential.
          </div>
        </div>
      </div>

      <div className="font-mono text-xs text-white/55 flex items-center gap-3" data-testid="era-cert-progress">
        <span>Held: <span className="text-[#D4A84A]">{data?.held_count ?? 0}</span> / {data?.total_count ?? 9}</span>
        {(data?.held_count ?? 0) === (data?.total_count ?? 9) && (
          <span className="text-emerald-300">Master unlocked</span>
        )}
      </div>

      {/* Master row */}
      <div
        className="border border-[#D4A84A]/30 rounded p-5 relative overflow-hidden"
        data-testid="master-cert-row"
      >
        {/* Combined accent stripe */}
        <div className="absolute top-0 left-0 right-0 flex h-1">
          {Object.values(ERA_STYLE).map((s, i) => (
            <div key={i} className="flex-1" style={{ background: s.accent }} />
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Award size={14} className="text-[#D4A84A]" />
              <span className="font-hud text-[10px] md:text-xs tracking-widest text-[#D4A84A] uppercase">Master Certificate</span>
            </div>
            <div className="font-historical text-xl md:text-2xl text-white mt-1.5">All nine eras. All chains. All contradictions.</div>
            {master.active_certificate ? (
              <div className="font-mono text-[10px] md:text-xs text-emerald-300 mt-2" data-testid="master-cert-active">
                Issued {new Date(master.active_certificate.issued_at).toLocaleDateString()} · Active
              </div>
            ) : master.ready_to_claim ? (
              <div className="font-mono text-[10px] md:text-xs text-[#2F8A92] mt-2">Ready to claim — every era is locked in.</div>
            ) : (
              <div className="font-mono text-[10px] md:text-xs text-white/55 mt-2">
                Missing: {master.missing_eras?.length ?? 9} era(s). Keep walking.
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {master.active_certificate ? (
              <>
                <a
                  href={`/api${master.active_certificate.verify_url_path}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="master-cert-verify-link"
                  className="font-hud text-[10px] md:text-xs tracking-widest text-white/85 border border-[#D4A84A]/40 px-3 py-1.5 hover:text-[#D4A84A] inline-flex items-center gap-1.5"
                >
                  <ExternalLink size={11} /> VERIFY
                </a>
                <a
                  href={`/api/certificates/${master.active_certificate.certificate_id}/pdf`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="master-cert-pdf-link"
                  className="font-hud text-[10px] md:text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/85 inline-flex items-center gap-1.5"
                >
                  <Download size={11} /> PDF
                </a>
              </>
            ) : (
              <button
                onClick={claimMaster}
                disabled={!master.ready_to_claim || claimingMaster}
                data-testid="master-cert-claim-btn"
                className="font-hud text-[10px] md:text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {claimingMaster ? 'CLAIMING…' : 'CLAIM MASTER'}
              </button>
            )}
          </div>
        </div>
        {master.active_certificate && (
          <div className="mt-3 pl-1">
            <CertPremiumActions certificate={master.active_certificate} testIdPrefix="master-cert" />
          </div>
        )}
      </div>

      {/* Per-era list */}
      <ul className="space-y-3" data-testid="era-cert-list">
        {eras.map((era) => {
          const style = ERA_STYLE[era.era] || {};
          const claimable = era.ready_to_claim && !era.active_certificate;
          return (
            <li
              key={era.era}
              data-testid={`era-row-${era.era}`}
              className="border border-[#D4A84A]/15 rounded p-4 relative overflow-hidden"
            >
              <div
                className="absolute top-0 left-0 bottom-0 w-1"
                style={{ background: style.accent || '#D4A84A' }}
              />
              <div className="pl-3 flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="font-hud text-[10px] md:text-xs tracking-widest uppercase"
                      style={{ color: style.accent }}
                    >
                      {era.label}
                    </span>
                    {era.active_certificate && (
                      <span className="font-mono text-[10px] text-emerald-300" data-testid={`era-active-${era.era}`}>
                        · ACTIVE
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] md:text-xs text-white/55 mt-0.5">{style.subtitle}</div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[10px] md:text-xs font-mono">
                    <div className="flex items-center gap-1.5">
                      {era.criteria_completed_all_chains
                        ? <CheckCircle2 size={11} className="text-[#2F8A92]" />
                        : <Circle size={11} className="text-white/30" />}
                      <span className="text-white/75">
                        Walked all chains in era
                        <span className="text-white/45"> ({era.completed_count}/{era.total_chains})</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {era.criteria_engaged_contradicting_source
                        ? <CheckCircle2 size={11} className="text-[#2F8A92]" />
                        : <Circle size={11} className="text-white/30" />}
                      <span className="text-white/75">Engaged a contradicting primary source</span>
                    </div>
                  </div>
                  {era.note && (
                    <div className="font-mono text-[10px] text-white/45 mt-1.5 italic">{era.note}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {era.active_certificate ? (
                    <>
                      <a
                        href={`/api${era.active_certificate.verify_url_path}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`era-verify-${era.era}`}
                        className="font-hud text-[10px] md:text-xs tracking-widest text-white/85 border border-[#D4A84A]/30 px-3 py-1.5 hover:text-[#D4A84A] inline-flex items-center gap-1.5"
                      >
                        <ExternalLink size={11} /> VERIFY
                      </a>
                      <a
                        href={`/api/certificates/${era.active_certificate.certificate_id}/pdf`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`era-pdf-${era.era}`}
                        className="font-hud text-[10px] md:text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/85 inline-flex items-center gap-1.5"
                      >
                        <Download size={11} /> PDF
                      </a>
                    </>
                  ) : (
                    <button
                      onClick={() => claimEra(era.era)}
                      disabled={!claimable || claimingEra === era.era}
                      data-testid={`era-claim-btn-${era.era}`}
                      className="font-hud text-[10px] md:text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {claimingEra === era.era ? 'CLAIMING…' : 'CLAIM'}
                    </button>
                  )}
                </div>
              </div>
              {era.active_certificate && (
                <div className="mt-3 pl-3">
                  <CertPremiumActions certificate={era.active_certificate} testIdPrefix={`era-${era.era}`} />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {msg && (
        <div className="font-mono text-xs md:text-sm text-[#2F8A92]" data-testid="era-cert-msg">{msg}</div>
      )}
    </div>
  );
}
